export const ASPIRE_INFLATED_WARNING =
  'Aspire forces every minute into a visit, so the last stop of the day absorbs leftover slack. ' +
  "Don't treat a single visit's value as real."

const T = (key, label, unit, basis, definition, caveat = null) => ({
  key, label, unit, basis, definition, caveat,
})

export const HOURS_TERMS = {
  internalEstimate: T('internalEstimate', 'Internal Estimate', 'man-hours', 'estimate',
    'Our on-site man-hours per visit over the trailing 12 months, dropping the single longest and shortest visit so outliers don’t skew it. With fewer than 4 visits it uses a plain average (or the Aspire value when there is no history).'),
  contractEstimate: T('contractEstimate', 'Contract Estimate', 'man-hours', 'estimate',
    'Man-hours per visit defined on the Aspire contract.'),
  medianOnSite: T('medianOnSite', 'Median On-Site', 'clock', 'actual',
    'The median of measured on-site time across this property’s clock rows.'),
  actualHours: T('actualHours', 'Actual Hours', 'clock', 'actual',
    'Aspire’s on-site plus drive time off the work ticket (ActHours).', 'aspire-inflated'),
  driveTime: T('driveTime', 'Drive Time', 'clock', 'actual',
    'Drive time derived from work-ticket visit data.', 'aspire-inflated'),
  estimatedDrive: T('estimatedDrive', 'Estimated Drive Time', 'clock', 'estimate',
    'Drive time predicted by our ORS routing engine.'),
  clockHours: T('clockHours', 'Clock Hours', 'clock', 'actual',
    'The raw punch span from clock-in to clock-out (e.g. 8:14–10:12 = 1:58).'),
}

export function getEntry(key) {
  return Object.prototype.hasOwnProperty.call(HOURS_TERMS, key) ? HOURS_TERMS[key] : null
}

const fmtH = (n) => String(Math.round(n * 100) / 100)

export function buildExample({ termKey, manHours, clockHours, crew, crewSource } = {}) {
  const entry = getEntry(termKey)
  if (!entry) return null
  if (!Number.isFinite(crew) || crew <= 0) return null

  let mh = manHours
  let ch = clockHours
  if (entry.unit === 'man-hours') {
    if (!Number.isFinite(mh)) return null
    ch = mh / crew
  } else {
    if (!Number.isFinite(ch)) return null
    mh = ch * crew
  }
  return {
    manHours: mh,
    clockHours: ch,
    crew,
    crewSource: crewSource || 'planned-default',
    display: `${fmtH(mh)} man-hrs = ${crew} crew × ${fmtH(ch)}h`,
  }
}

const num = (v) => (Number.isFinite(v) ? v : undefined)

function planningCrew(property) {
  const assigned = num(property.assignedRouteCrewSize)
  if (assigned > 0) return { crew: assigned, crewSource: 'planned-override' }
  const est = num(property.internalEstimateCrewCount)
  if (est > 0) return { crew: Math.round(est), crewSource: 'planned-default' }
  return { crew: 3, crewSource: 'planned-default' }
}

export function exampleFor(termKey, property) {
  if (!property) return null
  switch (termKey) {
    case 'internalEstimate': {
      const manHours = num(property.internalEstimatedManHours)
      if (manHours === undefined) return null
      const { crew, crewSource } = planningCrew(property)
      const ex = buildExample({ termKey, manHours, crew, crewSource })
      if (!ex) return null
      const source = property.internalEstimateSource
      const n = num(property.internalEstimateVisitCount)
      let note = null
      if (source === 'aspire') {
        note = 'from Aspire (no visit history)'
      } else if (n !== undefined && n > 0) {
        const visits = `${n} visit${n === 1 ? '' : 's'}`
        note = source === 'trimmedAvg' ? `from ${visits}, trimmed` : `from ${visits}, untrimmed`
      }
      return { ...ex, note }
    }
    // contractEstimate: no man-hours field on payload yet (only $ price) -> null.
    // medianOnSite/actualHours/driveTime/estimatedDrive/clockHours: per-visit crew +
    // clock rows not on payload yet -> null (needs-server-work, later task).
    default:
      return null
  }
}

// Bare numeric value for the glossary panel's "This property" column (or null).
// Today only internalEstimate is wireable; everything else returns null -> panel shows "—".
// The panel shows a single figure: the clock-hours side of the worked example.
export function liveValue(termKey, property) {
  if (!property) return null
  const entry = getEntry(termKey)
  if (!entry) return null
  const ex = exampleFor(termKey, property)
  if (!ex) return null
  // Intentionally clock-normalized for EVERY term (even man-hours terms like
  // internalEstimate/contractEstimate) so the glossary column is apples-to-apples.
  // Do not switch this to ex.manHours when wiring a future man-hours term.
  return ex.clockHours
}
